import React, {useRef, useState, useEffect, useCallback, MutableRefObject, useMemo} from "react";
import {toJS} from "mobx";
import {observer} from "mobx-react-lite";
import intl from "react-intl-universal";
import {useAppState, useUserState, useServiceRegistry} from "@stores/AppStateContext";

import {FileUploadsManager} from "@stores/models/FileUploadsManager";
import {TaskReportModel, TaskReportModelType, TaskReportFileMeta} from "@stores/taskLists/TaskReportModel";
import {TaskReportEditProps} from "../modal-names";

import CompleteTaskForm from "./CompleteTaskForm";
import CantCompleteTaskForm from "./CantCompleteTaskForm";
import {ModalBase} from "../components/ModalBase";
import {formatDayWithShortMonth} from "../../../../utils/date";
import {uploadTaskReportImage} from "../../../../utils/file/uploadTaskReportImage";

import {snackBarRef} from "@stores/snackbarRef";
import {screenSizeProvider} from "@stores/ScreenSizeProvider";


import "./TaskReportEdit.css";


export const TaskReportEdit = observer(({calendarTable, taskId, reportForDay}: TaskReportEditProps) => {
    const appState = useAppState();
    const userState = useUserState()!;
    const serviceRegistry = useServiceRegistry();
    const storageService = serviceRegistry.getStorageService();
    const baseData = reportForDay ? toJS(reportForDay) : {
        isNew: true,
        hasChanges: true,
        taskId,
        taskListUnitId: calendarTable.selectedTaskListsUnitId!,
        completedAt: new Date().toISOString(),
        comment: '',
        images: [],
        commentImages: [],
        completedBy: [userState!.currentUserId!],
        status: 'completed',
    };

    const taskReportEnv = useMemo(() => calendarTable.getTaskReportModelEnv(), [calendarTable]);
    const completeModelRef = useRef<TaskReportModelType>(TaskReportModel.create({...baseData, status: 'completed'}, taskReportEnv));
    const cantCompleteModelRef = useRef<TaskReportModelType>(TaskReportModel.create({...baseData, status: 'incomplete'}, taskReportEnv));

    const createUploadFn = useCallback((modelRef: MutableRefObject<TaskReportModelType>) =>
        async (file: File): Promise<TaskReportFileMeta> => {
            const {path, thumbPath} = await uploadTaskReportImage(
                file, 
                modelRef.current.taskListUnitId, 
                modelRef.current.taskId,
                storageService,
                userState!.tenantId
            );
            return {
                path,
                thumbPath,
                uploadedAt: new Date().toISOString(),
                userId: userState?.currentUserId!,
            };
        }, [storageService, userState]);


    const onError = useCallback(() => snackBarRef.current?.enqueueSnackbar({
        variant: 'error',
        message: intl.get('upload_image.error').d('Failed to upload image.'),
    }), []);

    const imagesFileUploadManager = useRef(new FileUploadsManager<TaskReportFileMeta>({
        uploadFn: createUploadFn(completeModelRef),
        onError,
        initialMetas: completeModelRef.current.images || [],
        storageService,
        fileProvider: userState.fileProvider,
        tenantId: userState.tenantId
    })).current;

    const completeCommentImagesFileUploadManager = useRef(new FileUploadsManager<TaskReportFileMeta>({
        uploadFn: createUploadFn(completeModelRef),
        onError,
        initialMetas: completeModelRef.current.commentImages || [],
        storageService,
        fileProvider: userState.fileProvider,
        tenantId: userState.tenantId
    })).current;

    const cantCompleteCommentImagesFileUploadManager = useRef(new FileUploadsManager<TaskReportFileMeta>({
        uploadFn: createUploadFn(cantCompleteModelRef),
        onError,
        initialMetas: cantCompleteModelRef.current.commentImages || [],
        storageService,
        fileProvider: userState.fileProvider,
        tenantId: userState.tenantId
    })).current;

    useEffect(() => {
        return () => {
            imagesFileUploadManager.destroy();
            completeCommentImagesFileUploadManager.destroy();
            cantCompleteCommentImagesFileUploadManager.destroy();
        };
    }, []);

    const [activeForm, setActiveForm] = useState<'complete' | 'cantComplete'>(reportForDay?.status === 'incomplete' ? 'cantComplete' : 'complete');

    const taskDate = completeModelRef.current.completedAt;

    const handleClose = () => {
        imagesFileUploadManager.clear();
        completeCommentImagesFileUploadManager.clear();
        cantCompleteCommentImagesFileUploadManager.clear();
        appState.setModal(null);
    };

    const isMobile = screenSizeProvider.view !== 'desktop';

    return (
        <ModalBase
            header={<span className="body-medium TaskReportEdit__headerDate">{formatDayWithShortMonth(taskDate)}</span>}
            open={true}
            onClose={handleClose}
            minWidth={isMobile ? '100%' : 640}
            maxWidth={'calc(min(640px, 100vw))'}
        >
            {activeForm === 'complete' ? (
                <CompleteTaskForm
                    modelRef={completeModelRef}
                    calendarTable={calendarTable}
                    taskId={taskId}
                    onCantComplete={() => setActiveForm('cantComplete')}
                    imagesFileUploadManager={imagesFileUploadManager}
                    commentImagesFileUploadManager={completeCommentImagesFileUploadManager}
                    fileProvider={userState!.fileProvider}
                />
            ) : (
                <CantCompleteTaskForm
                    modelRef={cantCompleteModelRef}
                    calendarTable={calendarTable}
                    taskId={taskId}
                    onGoBack={() => setActiveForm('complete')}
                    commentImagesFileUploadManager={cantCompleteCommentImagesFileUploadManager}
                    fileProvider={userState!.fileProvider}
                />
            )}
        </ModalBase>
    );
});

export default TaskReportEdit;
